import * as React from 'react';
import {ColumnProps} from 'antd/lib/table';
import {ErrorResponse, MaintenanceEntry, MaintenanceSheet, SaveItemAlertRequest} from '../api/dtos/dtos';
import locales from '../locales';
import {Button, Modal, Table} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import * as tableUtils from '../utils/table-utils';
import {simpleColumn} from '../utils/table-utils';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {showSaveSuccessNotification} from '../utils/dialogs';
import MaintenanceSheetForm from './MaintenanceSheetForm';
import {ItemAlertEditor, newAlertRequest} from "../alerts/ItemAlertEditor";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import ButtonBar from "../common/ButtonBar";
import accountProvider from "../account/AccountProvider";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";

interface Props {
  itemId: number;
}

interface ItemMaintenanceSheetEditorState {
  editing: boolean;
  editedAlert?: SaveItemAlertRequest;
  maintenance?: MaintenanceSheet;
  error?: ErrorResponse;
}

class MaintenanceSheetEditor extends React.Component<Props, ItemMaintenanceSheetEditorState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  public state: ItemMaintenanceSheetEditorState = {
    editing: false,
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {maintenance, editing, editedAlert, error} = this.state;
    const data = maintenance ? maintenance.entries : [];
    const admin = accountProvider.isAdmin();
    const columns: Array<ColumnProps<MaintenanceEntry>> = [
      simpleColumn(locales.appellation, p => p.key),
      simpleColumn(locales.content, p => p.value),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.maintenance, this)}
        </GlobalAlertContextConsumer>

        {editedAlert && (
          <ItemAlertEditor
            alert={editedAlert}
            onAlertSaved={this.onAlertSaved}
            onAlertCanceled={this.onAlertCanceled}
          />
        )}

        <Modal
          title={locales.edit}
          visible={editing}
          footer={null}
          onCancel={this.onFormCancel}
          destroyOnClose={true}
        >
          <MaintenanceSheetForm
            entries={data}
            onFormSubmit={this.onFormSubmit}
            onFormCancel={this.onFormCancel}
          />
        </Modal>

        <SectionHeaderFormItem title={locales.maintenance_sheet} style={{marginTop: 24}}/>

        <ButtonBar style={{marginBottom: 8}}>
          {admin &&
              <Button icon="plus" size="small" onClick={this.onAddAlertClick}>{locales.add_alert}</Button>}
          <Button icon="edit" size="small" type="primary" onClick={this.onEditClick}>{locales.edit}</Button>
        </ButtonBar>

        <Table
          dataSource={data}
          columns={columns}
          rowKey={tableUtils.indexRowKeyFn}
          bordered={true}
          showHeader={false}
          pagination={false}
          size="small"
          scroll={{x: 512}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private onAddAlertClick = () => {
    this.setState({editedAlert: newAlertRequest(this.props.itemId)});
  };

  private onAlertSaved = () => {
    this.alertController?.refreshAlertCount();
    this.onAlertCanceled();
  };

  private onAlertCanceled = () => {
    this.setState({editedAlert: undefined});
  };

  private onEditClick = () => {
    this.setState({editing: true});
  };

  private onFormCancel = () => {
    this.setState({editing: false});
  };

  private onFormSubmit = (maintenance: MaintenanceSheet) => {
    const {itemId} = this.props;
    withLoadingOverlay(
      furoApi.items.saveMaintenanceSheet(itemId, maintenance)
        .then(this.onSaveSuccess)
        .catch(this.onApiError), this.loadingHandler);
  };

  private onSaveSuccess = () => {
    this.onFormCancel();
    showSaveSuccessNotification();
    this.reload();
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private reload = () => {
    const {itemId} = this.props;
    withLoadingOverlay(
      furoApi.items.getMaintenanceSheet(itemId)
        .then(this.onReloadSuccess)
        .catch(this.onApiError), this.loadingHandler);
  };

  private onReloadSuccess = (maintenance: MaintenanceSheet) => {
    this.setState({
      maintenance,
      error: undefined,
    });
  };
}

export default MaintenanceSheetEditor;


// WEBPACK FOOTER //
// ./src/item-details/MaintenanceSheetEditor.tsx